/**
 * Real Conversion Client using tRPC
 *
 * This client connects to:
 * - kernel-plugin-invoice-outbound for JSON → PDF/E-Invoice
 * - kernel-plugin-invoice-inbound for E-Invoice → JSON
 *
 * It implements the InvoiceConversionApiClient interface.
 */

import {
  type CatalogCountry,
  type ConversionResult,
  type FormatDetectionOutcome,
  type GenerationWarning,
  getSampleTemplate,
  type InvoiceConversionApiClient,
} from '@xhubio-saas/invoice-frontend/plugin-invoice-api'
import { useMemo, useRef } from 'react'
import { trpc } from '@/trpc'

/**
 * Static fallback catalog for the tRPC client. The tRPC client is only used
 * while the temp API key is loading — once the key is available, the REST
 * client fetches the full catalog from GET /api/v1/invoice/formats (single
 * source of truth). This snapshot covers the legacy tRPC-supported countries.
 */
const FALLBACK_CATALOG: CatalogCountry[] = (
  [
    ['DE', 'Germany (DE)', ['zugferd:ZUGFeRD 2.3/2.4/2.5', 'xrechnung:XRechnung 3.0/3.0.2']],
    ['AT', 'Austria (AT)', ['ebinterface:ebInterface', 'ubl:UBL (Peppol BIS 3.0)']],
    ['CH', 'Switzerland (CH)', ['zugferd:ZUGFeRD (CH)', 'qr-bill:QR-Bill']],
    ['BE', 'Belgium (BE)', ['ubl:UBL (Peppol BIS 3.0)', 'facturx:Factur-X']],
    ['NL', 'Netherlands (NL)', ['ubl:UBL (Peppol BIS 3.0 / NL-CIUS)']],
    ['FR', 'France (FR)', ['facturx:Factur-X 1.0/1.08/1.09', 'ubl:UBL (Peppol BIS 3.0)']],
    ['IT', 'Italy (IT)', ['fatturapa:FatturaPA 1.2.3']],
    ['ES', 'Spain (ES)', ['facturae:Facturae', 'ubl:UBL (Peppol BIS 3.0)']],
    ['CZ', 'Czech Republic (CZ)', ['isdoc:ISDOC', 'ubl:UBL (Peppol BIS 3.0)']],
    ['HU', 'Hungary (HU)', ['nav:NAV']],
    ['PL', 'Poland (PL)', ['ubl:KSeF FA(3) (national UBL slot)']],
    ['PT', 'Portugal (PT)', ['ubl:CIUS-PT (national UBL)']],
    ['RO', 'Romania (RO)', ['ubl:eFactura (CIUS-RO)']],
    ['BG', 'Bulgaria (BG)', ['ubl:UBL (Peppol BIS 3.0)']],
  ] as Array<[string, string, string[]]>
).map(([code, label, formats]) => ({
  code,
  label,
  formats: [
    { code: 'pdf', label: 'PDF', kind: 'pdf' as const },
    ...formats.map((entry) => {
      const [formatCode, formatLabel] = entry.split(/:(.+)/) as [string, string]
      return { code: formatCode, label: formatLabel, kind: 'einvoice' as const }
    }),
  ],
}))

/**
 * parseStrict (tRPC) still expects the legacy frontend format ids. Maps the
 * API format token (+ country for the country-specific `ubl` slots) to the
 * legacy id. Only used by the tRPC fallback client — the REST parse endpoint
 * takes the API token directly.
 */
export function legacyParseFormat(sourceFormat: string, countryCode: string): string | undefined {
  const byCountry: Record<string, string> = {
    'BE:ubl': 'ubl-be',
    'NL:ubl': 'ubl-nl',
    'PL:ubl': 'ksef',
    'PT:ubl': 'saf-t',
    'RO:ubl': 'efactura',
    'BG:ubl': 'ubl-bg',
  }
  const byFormat: Record<string, string> = {
    xrechnung: 'xrechnung-3.0',
    zugferd: 'zugferd-2.3',
    facturx: 'factur-x',
    ebinterface: 'ebinterface',
    'qr-bill': 'qr-rechnung',
    fatturapa: 'fatturapa',
    facturae: 'facturae',
    isdoc: 'isdoc',
    nav: 'nav',
  }
  return byCountry[`${countryCode.toUpperCase()}:${sourceFormat}`] ?? byFormat[sourceFormat]
}

/**
 * Convert Uint8Array to Base64 string
 */
export function uint8ArrayToBase64(data: Uint8Array): string {
  let binary = ''
  const len = data.byteLength
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(data[i]!)
  }
  return btoa(binary)
}

/**
 * Map country names to ISO 3166-1 alpha-2 codes
 */
export const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  deutschland: 'DE',
  germany: 'DE',
  österreich: 'AT',
  austria: 'AT',
  schweiz: 'CH',
  switzerland: 'CH',
  belgien: 'BE',
  belgium: 'BE',
  niederlande: 'NL',
  netherlands: 'NL',
  frankreich: 'FR',
  france: 'FR',
  italien: 'IT',
  italy: 'IT',
  spanien: 'ES',
  spain: 'ES',
  portugal: 'PT',
  polen: 'PL',
  poland: 'PL',
  tschechien: 'CZ',
  'czech republic': 'CZ',
  ungarn: 'HU',
  hungary: 'HU',
  rumänien: 'RO',
  romania: 'RO',
  bulgarien: 'BG',
  bulgaria: 'BG',
}

/**
 * Convert country name or code to ISO 3166-1 alpha-2 code
 */
export function normalizeCountryCode(value: string | undefined): string | undefined {
  if (!value) return undefined
  // If already a 2-letter code, return uppercase
  if (value.length === 2) return value.toUpperCase()
  // Try to map country name to code
  const normalized = value.toLowerCase().trim()
  return COUNTRY_NAME_TO_CODE[normalized] || value
}

/**
 * Normalize a party object to the expected lib-invoice-interface format.
 * Handles both nested address format and flat format.
 */
// biome-ignore lint/suspicious/noExplicitAny: input shape is duck-typed across many caller variants
export function normalizeParty(party: any): any {
  if (!party) return party

  let result = { ...party }

  // If using nested address format, flatten it
  if (party.address) {
    const { address, ...rest } = party
    result = {
      ...rest,
      street: address.street || party.street,
      additionalStreet: address.additionalStreet || party.additionalStreet,
      postalCode: address.postalCode || party.postalCode,
      city: address.city || party.city,
      countryCode: address.country || address.countryCode || party.countryCode,
      state: address.state || party.state,
    }
  }

  // If has country but not countryCode, use country
  if (result.country && !result.countryCode) {
    result.countryCode = result.country
  }

  // Normalize countryCode to ISO format
  if (result.countryCode) {
    result.countryCode = normalizeCountryCode(result.countryCode)
  }

  return result
}

/**
 * Normalize invoice data to the expected lib-invoice-interface format.
 * Handles various input formats and transforms them to the backend's expected structure.
 */
// biome-ignore lint/suspicious/noExplicitAny: input shape is duck-typed across many caller variants
export function normalizeInvoice(invoice: any): any {
  if (!invoice) return invoice

  // Map customer to buyer if buyer doesn't exist
  const buyer = invoice.buyer || invoice.customer

  // Map lineItems to items if items doesn't exist
  const items = invoice.items || invoice.lineItems

  return {
    ...invoice,
    seller: normalizeParty(invoice.seller),
    buyer: normalizeParty(buyer),
    items,
    // Normalize date fields if needed
    issueDate: invoice.issueDate || invoice.invoiceDate,
    dueDate: invoice.dueDate,
  }
}

/**
 * Creates an Conversion client that uses tRPC.
 *
 * Must be called within a React component inside TRPCProvider.
 */
/**
 * Die Warnungen einer `generate`-Antwort in der Form, die die Karte zeigt (Plan
 * `API/2026-09-23/04` S7). Der Text des Servers bleibt unveraendert — ein Normtext
 * (`[BR-…]`) wird nie uebersetzt.
 */
export function generationWarnings(
  warnings: ReadonlyArray<{ code: string; message: string; field?: string }> | undefined
): GenerationWarning[] {
  return (warnings ?? []).map((w) => ({
    code: w.code,
    message: w.message,
    ...(w.field && { field: w.field }),
  }))
}

export function useInvoiceClient(): InvoiceConversionApiClient {
  // Get mutation for outbound generation (JSON → PDF/E-Invoice)
  const generateMutation = trpc.invoiceOutbound.generate.useMutation()

  // Get mutation for inbound parsing (E-Invoice → JSON) with strict format validation
  const parseStrictMutation = trpc.invoiceInbound.parseStrict.useMutation()

  // Formaterkennung als Mutation (POST): eine Query traegt den Beleg in der GET-URL, und ab 2048 Zeichen verweigert
  // der httpBatchLink die Anfrage (Plan API 10-10/09, E9). Sie liest nur und entwertet deshalb nichts — es gibt keinen
  // Lesezweig, dessen Stand sie aendert.
  const detectDocumentFormatMutation = trpc.invoiceInbound.detectDocumentFormat.useMutation()

  // Die Mutationen wandern in eine Ref: ihr Inhalt ist pro Render neu, die
  // Ref-Identitaet bleibt stabil. Gelesen wird `.current` NUR im asynchronen
  // Callback, nie waehrend des Renderns.
  const mutations = useRef({
    generateMutation,
    parseStrictMutation,
    detectDocumentFormatMutation,
  })
  mutations.current = {
    generateMutation,
    parseStrictMutation,
    detectDocumentFormatMutation,
  }

  return useMemo(
    () => ({
      async convertToPdf({ json, template, countryCode }): Promise<ConversionResult> {
        try {
          const rawInvoice = JSON.parse(json)
          const invoice = normalizeInvoice(rawInvoice)

          // The raw tRPC generate procedure has no server-side default-template
          // resolution — that lives only in the REST layer. Mirror it client-side
          // so "No Template (uses system default)" also converts in this fallback
          // path; getSampleTemplate matches the system default per country.
          const effectiveTemplate = template ?? JSON.stringify(getSampleTemplate(countryCode))

          const result = await mutations.current.generateMutation.mutateAsync({
            invoice,
            countryCode,
            format: 'pdf',
            template: effectiveTemplate,
          })

          if (!result.success) {
            return {
              success: false,
              error: result.errors?.[0]?.message || 'PDF generation failed',
            }
          }

          // Convert result.data to Uint8Array
          // tRPC serializes Uint8Array as an object with numeric keys {0: 60, 1: 63, ...}
          let dataArray: Uint8Array
          if (result.data instanceof Uint8Array) {
            dataArray = result.data
          } else if (Array.isArray(result.data)) {
            dataArray = new Uint8Array(result.data)
          } else if (typeof result.data === 'object') {
            // Convert object {0: 60, 1: 63, ...} to array
            const dataObj = result.data as unknown as Record<string, number>
            const values = Object.keys(dataObj)
              .sort((a, b) => Number(a) - Number(b))
              .map((key) => dataObj[key] ?? 0)
            dataArray = new Uint8Array(values)
          } else {
            dataArray = new Uint8Array(0)
          }

          // Convert Uint8Array to Base64
          const base64Data = uint8ArrayToBase64(dataArray)

          return {
            success: true,
            data: base64Data,
            contentType: result.mimeType || 'application/pdf',
            filename: result.filename || `${invoice.invoiceNumber}.pdf`,
          }
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'PDF conversion failed',
          }
        }
      },

      async convertToEInvoice({
        json,
        format,
        formatOptions,
        template,
        countryCode,
      }): Promise<ConversionResult> {
        try {
          const rawInvoice = JSON.parse(json)
          const invoice = normalizeInvoice(rawInvoice)

          // ZUGFeRD and Factur-X require template (PDF with embedded XML)
          const isPdfWithEmbeddedXml = format === 'zugferd' || format === 'facturx'
          if (isPdfWithEmbeddedXml && !template) {
            return {
              success: false,
              error: `${format === 'zugferd' ? 'ZUGFeRD' : 'Factur-X'} generation requires a PDF template`,
            }
          }

          const result = await mutations.current.generateMutation.mutateAsync({
            invoice,
            countryCode,
            // biome-ignore lint/suspicious/noExplicitAny: catalog format token is a string, backend wants enum
            format: format as any,
            // Include template for ZUGFeRD/Factur-X
            ...(isPdfWithEmbeddedXml && template ? { template } : {}),
            // Version-specific options come from the selected FormatOption
            ...(formatOptions && Object.keys(formatOptions).length > 0 ? { formatOptions } : {}),
          })

          if (!result.success) {
            return {
              success: false,
              error: result.errors?.[0]?.message || 'E-Invoice generation failed',
            }
          }

          // Convert result.data to Uint8Array
          // tRPC serializes Uint8Array as an object with numeric keys {0: 60, 1: 63, ...}
          let dataArray: Uint8Array
          if (result.data instanceof Uint8Array) {
            dataArray = result.data
          } else if (Array.isArray(result.data)) {
            dataArray = new Uint8Array(result.data)
          } else if (typeof result.data === 'object') {
            // Convert object {0: 60, 1: 63, ...} to array
            const dataObj = result.data as unknown as Record<string, number>
            const values = Object.keys(dataObj)
              .sort((a, b) => Number(a) - Number(b))
              .map((key) => dataObj[key] ?? 0)
            dataArray = new Uint8Array(values)
          } else {
            dataArray = new Uint8Array(0)
          }

          // Plan `API/2026-09-23/04` S7: die Warnungen des Erzeugers und seiner Selbstpruefung
          // (`SELF_CHECK_INCOMPLETE`, im App-Weg auch Normbefunde `SPEC_RULE_FAILED`) reisen
          // mit — bis hierher warf dieser Client sie weg, und die Karte zeigte ein gruenes
          // Ergebnis ohne Hinweis.
          const warnings = generationWarnings(result.warnings)
          const hinweise = warnings.length > 0 ? { warnings } : {}

          // ZUGFeRD/Factur-X returns PDF, other formats return XML
          if (isPdfWithEmbeddedXml) {
            // Convert to Base64 for PDF
            const base64Data = uint8ArrayToBase64(dataArray)
            return {
              success: true,
              data: base64Data,
              contentType: result.mimeType || 'application/pdf',
              filename: result.filename || `${invoice.invoiceNumber}_${format}.pdf`,
              // Include embedded XML if returned by backend
              embeddedXml: result.embeddedXml,
              ...hinweise,
            }
          }

          // Decode to XML string for other formats
          const decoder = new TextDecoder('utf-8')
          const xmlData = decoder.decode(dataArray)

          return {
            success: true,
            data: xmlData,
            contentType: result.mimeType || 'application/xml',
            filename: result.filename || `${invoice.invoiceNumber}-${format}.xml`,
            ...hinweise,
          }
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'E-Invoice conversion failed',
          }
        }
      },

      async convertToJson({ file, sourceFormat, countryCode }): Promise<ConversionResult> {
        try {
          const legacyFormat = legacyParseFormat(sourceFormat, countryCode)
          if (!legacyFormat) {
            return {
              success: false,
              error: `Parsing ${sourceFormat} (${countryCode}) requires the REST API client`,
            }
          }

          const content = await file.text()

          // Determine MIME type from file
          const mimeType = file.type === 'application/pdf' ? 'application/pdf' : 'application/xml'

          // Call inbound parser with strict format validation
          // parseStrict validates that the document matches the expected format
          const result = await mutations.current.parseStrictMutation.mutateAsync({
            document: {
              id: crypto.randomUUID(),
              content,
              mimeType,
              filename: file.name,
              metadata: {
                receivedAt: new Date().toISOString(),
                channel: 'UPLOAD',
                originalFilename: file.name,
              },
            },
            // biome-ignore lint/suspicious/noExplicitAny: legacy id mapping vs backend enum
            sourceFormat: legacyFormat as any,
          })

          if (!result.success) {
            // Return error message - structured errors are in result.errors
            const errorMessage = result.errors?.[0]?.message || 'E-Invoice parsing failed'
            return {
              success: false,
              error: errorMessage,
            }
          }

          // Format the parsed invoice as JSON
          // Discriminated union narrowing isn't tight enough here — after the
          // !success early return, two failure-variants are eliminated, but TS
          // still sees the third (FormatMismatch) which has no `invoice`.
          const successResult = result as Extract<typeof result, { invoice?: unknown }>
          const jsonData = JSON.stringify(successResult.invoice, null, 2)
          const invoiceNumber = successResult.invoice?.invoiceNumber || 'invoice'

          return {
            success: true,
            data: jsonData,
            contentType: 'application/json',
            filename: `${invoiceNumber}-parsed.json`,
          }
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'E-Invoice to JSON conversion failed',
          }
        }
      },

      /**
       * Quellformat-Erkennung fuer die Konvertierungs-Karte.
       *
       * Die Karte fragt zu einem Zeitpunkt, den der Nutzer bestimmt (Beleg
       * eingefuegt), nicht beim Rendern. Ein PDF kommt als Base64 mit
       * `mimeType: 'application/pdf'` — nur dann dekodiert der Server und liest
       * das eingebettete XML (Plan API 10-10/09, E5/E15); ohne `mimeType` kein Raten.
       *
       * Ein fehlendes Recht (`invoice-inbound:invoice:detect` — Viewer hat es
       * nicht) wirft hier; die Karte faengt das ab, zeigt es an und laesst die
       * Wahl von Hand offen. Deshalb wird der Fehler NICHT verschluckt.
       */
      async detectFormat({ content, countryCode, mimeType }): Promise<FormatDetectionOutcome> {
        const ergebnis = await mutations.current.detectDocumentFormatMutation.mutateAsync({
          content,
          countryCode,
          ...(mimeType && { mimeType }),
        })
        return ergebnis as FormatDetectionOutcome
      },

      async getCountryFormatCatalog(): Promise<CatalogCountry[]> {
        // Static fallback while the temp API key loads — the REST client
        // replaces this with the live backend catalog.
        return FALLBACK_CATALOG
      },
    }),
    // Die Mutationen liegen in der Ref (stabile Identitaet, `.current` nur im
    // asynchronen Callback gelesen) — dafuer braucht der Memo nichts. Seit die
    // Erkennung eine Mutation ist, liest der Client keine Query mehr.
    []
  )
}
